<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Tutorials" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search tutorials..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/tutorials/create" v-if="isSuperAdmin" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Tutorial</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Title</th>
                  <th scope="col">Service</th>
                  <th scope="col">Order</th>
                  <th scope="col">Status</th>
                  <th scope="col" v-if="isSuperAdmin">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="6" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredTutorials.length === 0">
                  <td colspan="6" class="text-center py-4">No tutorials found.</td>
                </tr>
                <tr v-else v-for="tutorial in filteredTutorials" :key="tutorial.id">
                  <td>#{{ tutorial.id }}</td>
                  <td>
                    <h6 class="mb-0 fs-14 fw-medium">{{ tutorial.title }}</h6>
                  </td>
                  <td class="text-body">{{ tutorial.service ? tutorial.service.service_name : '-' }}</td>
                  <td class="text-body">{{ tutorial.order }}</td>
                  <td>
                    <span class="badge" :class="tutorial.is_active ? 'bg-success' : 'bg-danger'">
                      {{ tutorial.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td v-if="isSuperAdmin">
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/tutorials/${tutorial.id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deleteTutorial(tutorial.id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import tutorialService from '@/services/tutorialService';

export default defineComponent({
  name: 'TutorialsPage',
  components: {
    PageTitle
  },
  setup() {
    const tutorials = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const isSuperAdmin = computed(() => {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      try {
        const user = JSON.parse(userStr);
        return user.role === 'super_admin';
      } catch (e) {
        return false;
      }
    });

    const fetchTutorials = async () => {
      loading.value = true;
      try {
        const response = await tutorialService.getAll();
        tutorials.value = response.data;
      } catch (error) {
        console.error('Error fetching tutorials:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteTutorial = async (id: number) => {
      if (confirm('Are you sure you want to delete this tutorial?')) {
        try {
          await tutorialService.delete(id);
          fetchTutorials();
        } catch (error) {
          console.error('Error deleting tutorial:', error);
        }
      }
    };

    const filteredTutorials = computed(() => {
      if (!searchQuery.value) return tutorials.value;
      const q = searchQuery.value.toLowerCase();
      return tutorials.value.filter(t => 
        t.title.toLowerCase().includes(q) || 
        (t.service && t.service.service_name.toLowerCase().includes(q))
      );
    });

    onMounted(() => {
      fetchTutorials();
    });

    return {
      tutorials,
      loading,
      searchQuery,
      filteredTutorials,
      deleteTutorial,
      isSuperAdmin
    };
  }
});
</script>
